import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ShoppingItemRow from "./ShoppingItemRow";
import DateSelector from "./DateSelector";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const ShoppingListForm = ({ initialData = null, onSuccess = () => {} }) => {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [shoppingDate, setShoppingDate] = useState(null);
  const [items, setItems] = useState([]);
  const [supplyOptions, setSupplyOptions] = useState([]);
  const itemRefs = useRef([]);
  const snackbar = useSnackbarAlert();
  const isEdit = Boolean(initialData);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const autoSaveTimeout = useRef(null);

  useEffect(() => {
    const fetchStores = async () => {
      const res = await api.get("/admin/stores");
      setStores(res.data);
    };
    fetchStores();
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      const [ingredientsRes, suppliesRes] = await Promise.all([
        api.get("/admin/ingredients"),
        api.get("/admin/supply-items"),
      ]);

      const grouped = [
        ...ingredientsRes.data.map((item) => ({
          ...item,
          label: item.name,
          group: "Ingredients",
          type: "ingredient",
          store: item.supplier?.name || null,
        })),
        ...suppliesRes.data.map((item) => ({
          ...item,
          label: item.name,
          group: "Supply Items",
          type: "supplyItem",
          store: item.supplier?.name || null,
        })),
      ];

      setSupplyOptions(grouped);
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (!initialData || supplyOptions.length === 0) return;

    setStoreId(initialData.storeId?._id || initialData.storeId);
    setShoppingDate(initialData.shoppingDate);

    const rebuilt = initialData.items.map((i) => {
      const matched =
        i.type === "supplyItem"
          ? supplyOptions.find((opt) => opt._id === i.supplyItemId?.toString())
          : i.type === "ingredient"
          ? supplyOptions.find((opt) => opt._id === i.ingredientId?.toString())
          : null;

      return {
        id: Date.now() + Math.random(),
        name: i.name,
        type: i.type || "custom",
        quantity: i.quantity,
        unit: i.unit,
        note: i.note || "",
        store: i.store || matched?.store || null,
        supplyItem: i.type === "supplyItem" ? matched : null,
        ingredient: i.type === "ingredient" ? matched : null,
        supplyItemId:
          i.supplyItemId ?? (i.type === "supplyItem" ? matched?._id : null),
        ingredientId:
          i.ingredientId ?? (i.type === "ingredient" ? matched?._id : null),
      };
    });

    setItems(rebuilt);
  }, [initialData, supplyOptions]);

  useEffect(() => {
    if (isEdit) triggerAutoSave();
  }, [storeId, shoppingDate, items]);

  const addNewItem = () => {
    // Collapse existing rows
    itemRefs.current.forEach((ref) => ref?.collapse?.());

    // Allow adding the first item without validation
    if (items.length === 0) {
      const newItem = {
        id: Date.now(),
        name: "",
        quantity: 1,
        unit: "",
        note: "",
        type: "custom",
        store: null,
        supplyItem: null,
        ingredient: null,
      };
      setItems([newItem]);
      return;
    }

    const lastItem = items[items.length - 1];

    // Validate the last item before allowing a new one
    const isInvalid =
      !lastItem?.name?.trim() || !lastItem?.unit || !lastItem?.quantity;

    if (isInvalid) {
      snackbar.error(
        "Please complete the last item or delete it before adding a new one."
      );
      return;
    }

    const newItem = {
      id: Date.now(),
      name: "",
      quantity: 1,
      unit: "",
      note: "",
      type: "custom",
      store: null,
      supplyItem: null,
      ingredient: null,
    };

    setItems((prev) => [...prev, newItem]);
  };

  const updateItem = (index, updated) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], ...updated };
    setItems(updatedItems);
  };

  const removeItem = (index) => {
    itemRefs.current.splice(index, 1);
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerAutoSave = () => {
    if (!isEdit || !initialData?._id) return;

    clearTimeout(autoSaveTimeout.current);

    autoSaveTimeout.current = setTimeout(async () => {
      // Check for incomplete items
      const hasIncompleteItem = items.some((i) => {
        if (!i.unit || !i.quantity) return true;

        const isSupplyItem = i.type === "supplyItem";
        const isIngredient = i.type === "ingredient";
        const isCustom = i.type === "custom";

        if (isSupplyItem && !(i.supplyItem || i.supplyItemId)) return true;
        if (isIngredient && !(i.ingredient || i.ingredientId)) return true;
        if (isCustom && !i.name?.trim()) return true;

        return false;
      });

      if (hasIncompleteItem) {
        console.warn("⛔ Auto-save skipped due to incomplete items:", items);
        return;
      }

      try {
        setIsAutoSaving(true);

        const payload = {
          storeId,
          shoppingDate,
          items: items.map((i) => ({
            name: i.name || "(Unnamed Item)",
            type: i.type || "custom",
            quantity: i.quantity,
            unit: i.unit,
            note: i.note || "",
            store: i.store || null,
            supplyItemId:
              i.type === "supplyItem"
                ? i.supplyItem?._id || i.supplyItemId || null
                : null,
            ingredientId:
              i.type === "ingredient"
                ? i.ingredient?._id || i.ingredientId || null
                : null,
          })),
        };

        console.log("🧾 Auto-saving payload:", payload);

        await api.put(`/admin/shopping-lists/${initialData._id}`, payload);
      } catch (err) {
        const status = err?.response?.status;
        console.error("Auto-save failed:", err.message || err);

        if (status === 401 || status === 403) {
          snackbar.error("Session expired. Please refresh or log in again.");
        } else {
          snackbar.error(
            "Auto-save failed. Please check your input or connection."
          );
        }
      } finally {
        setIsAutoSaving(false);
      }
    }, 1000);
  };

  const handleSubmit = async () => {
    if (!storeId) return snackbar.error("Please select a store");
    if (!shoppingDate) return snackbar.error("Please select a shopping date");
    if (items.length === 0) return snackbar.error("Add at least one item");

    const payload = {
      storeId,
      shoppingDate,
      items: items.map((i) => ({
        name: i.name || "(Unnamed Item)",
        type: i.type || "custom",
        quantity: i.quantity,
        unit: i.unit,
        note: i.note || "",
        store: i.store || null,
        supplyItemId:
          i.type === "supplyItem"
            ? i.supplyItem?._id || i.supplyItemId || null
            : null,
        ingredientId:
          i.type === "ingredient"
            ? i.ingredient?._id || i.ingredientId || null
            : null,
      })),
    };

    try {
      if (isEdit) {
        await api.put(`/admin/shopping-lists/${initialData._id}`, payload);
        snackbar.success("Shopping list updated.");
      } else {
        await api.post("/admin/shopping-lists", payload);
        snackbar.success("Shopping list submitted.");
      }
      onSuccess?.();
      setItems([]);
      setShoppingDate(null);
      setStoreId("");
      itemRefs.current = [];
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";

      console.error("Save failed:", msg);

      snackbar.error(
        isEdit
          ? `Failed to update list: ${msg}`
          : `Failed to submit list: ${msg}`
      );
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Weekly Grocery List
      </Typography>

      <FormControl size="small" fullWidth sx={{ maxWidth: 320, mb: 3, mt: 2 }}>
        <InputLabel id="store-select-label">Select Store</InputLabel>
        <Select
          labelId="store-select-label"
          label="Select Store"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          sx={{
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}>
          {stores.map((store) => (
            <MenuItem key={store._id} value={store._id}>
              {store.displayName || store.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ maxWidth: 320, mb: 3 }}>
        <DateSelector value={shoppingDate} onChange={setShoppingDate} />
      </Box>

      <Divider sx={{ my: 2 }} />

      {items.map((item, index) => (
        <ShoppingItemRow
          key={item.id}
          ref={(el) => (itemRefs.current[index] = el)}
          item={item}
          onChange={(updated) => updateItem(index, updated)}
          onRemove={() => removeItem(index)}
          autoExpand={index === items.length - 1}
          supplyOptions={supplyOptions}
        />
      ))}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={addNewItem}
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: 600,
            color: "#ff6b5c",
            borderColor: "#ff6b5c",
            "&:hover": {
              backgroundColor: "#fff5f3",
              borderColor: "#ff4f3c",
            },
          }}>
          Add Item
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            background: "linear-gradient(135deg, #ff7b6b, #ff4f3c)",
            color: "#fff",
            borderRadius: 2,
            px: 3,
            fontWeight: 600,
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            "&:hover": {
              background: "linear-gradient(135deg, #ff6857, #ff2f1e)",
            },
          }}>
          Submit List
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: "italic", mt: 1, color: "green" }}>
          {isAutoSaving ? "Saving..." : "All changes saved"}
        </Typography>
      </Box>
    </Box>
  );
};

export default ShoppingListForm;
